import React from 'react';

import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';

import { cn } from './utils';


const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-semibold '
  + 'transition-colors outline-none focus-visible:ring-focus disabled:pointer-events-none '
  + 'disabled:opacity-40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg]:size-4',
  {
    variants: {
      variant: {
        primary: 'bg-brand-gradient text-on-primary hover:opacity-90',
        secondary: 'bg-surface-2 text-ink border border-hairline-strong hover:bg-surface-3',
        ghost: 'bg-transparent text-ink-muted hover:bg-surface-2 hover:text-ink',
        danger: 'bg-danger text-white hover:opacity-90',
      },
      size: {
        md: 'h-10 px-4 py-[9px]',
        sm: 'h-8 px-3 text-[13px]',
        lg: 'h-12 px-[22px] text-[15px]',
        icon: 'size-10',
        fab: 'size-[52px] rounded-full [&_svg]:size-6',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

const Button = React.forwardRef((props, ref) => {
  const {
    asChild = false,
    className,
    variant,
    size,
    loading = false,
    disabled = false,
    children,
    type,
    ...restProps
  } = props;

  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      ref={ref}
      // Default to type="button" so a Button inside a <form> never triggers a
      // spurious submit; Submit passes type="submit" explicitly.
      disabled={disabled || loading}
      type={asChild ? type : (type ?? 'button')}
      className={cn(buttonVariants({ variant, size }), className)}
      {...restProps}
    >
      {/* In asChild mode Comp is Radix Slot, which requires EXACTLY ONE React
          element child (the consumer's element). Emitting the loading node as a
          sibling — even when it is `null` — makes Slot's children an array and
          throws "Expected a single React element child". So asChild passes the
          child straight through; the loading spinner only applies to the plain
          <button> path (asChild link-outs don't show a loading state). */}
      {asChild
        ? children
        : (
          <>
            {loading
              ? (
                <Loader2 className="animate-spin" />
              )
              : null}

            {children}
          </>
        )}
    </Comp>
  );
});

Button.displayName = 'Button';

export { Button, buttonVariants };
export default Button;
